// Keeping a screen current without the agent pressing F5.
//
// WHAT WAS WRONG BEFORE. Every screen in this app loaded once and then stopped. A
// ticket that arrived by email appeared in the queue only after a manual reload, and
// an agent watching for work had no way to know whether an empty queue meant "nothing
// came in" or "this page is four minutes old". For a support desk that is the whole
// job, so it is not a nicety.
//
// WHY POLLING AND NOT SSE OR A SOCKET. Three reasons, in order of weight:
//
//   1. SQL Server has no LISTEN/NOTIFY. Postgres can push a change out of the
//      database; here something must ASK, so the only question is who polls and how
//      often — a server holding an event stream open would be polling the database on
//      the client's behalf, with a connection held open per viewer for the privilege.
//
//   2. Over HTTP/1.1 a browser allows six connections per origin, and an EventSource
//      holds one open for as long as the tab lives. Two tabs on the same desk and a
//      third of the budget is gone before the first request. That is survivable with
//      HTTP/2 and unpleasant to debug when a proxy downgrades it.
//
//   3. A poll that fails is a poll that is retried. There is no reconnect logic, no
//      backfill-since-cursor, and no "did we miss an event while disconnected"
//      question, because every refresh fetches current state rather than a delta.
//
// The cost is latency measured in seconds, which is the right trade for a queue a
// human reads. If it ever stops being the right trade, the seam to change is this file
// alone: every caller passes a function that fetches current state, and an event
// stream would call exactly the same function.
//
// WHAT MAKES IT CHEAP. A hidden tab does not poll at all. Agents leave the queue open
// in a background tab all day, and polling those is pure waste — so the timer stops on
// `visibilitychange` and the screen refreshes the moment the tab is looked at again,
// which is the instant its freshness starts to matter.

import { useEffect, useRef } from 'react';

/** How often a visible screen re-asks, unless a caller says otherwise. */
export const DEFAULT_REFRESH_MS = 20_000;

/**
 * Whether a tick should fetch, and why not when it should not.
 *
 * Pure, and separated from the hook, because this is the part with rules in it and a
 * React hook cannot be tested in this project — there is no component-test harness, so
 * a decision left inside `useEffect` is a decision nothing can check. The hook below is
 * deliberately thin enough to read in one pass.
 *
 * @param {object} state
 * @param {boolean} state.visible    Is the tab being looked at?
 * @param {boolean} state.inFlight   Is a refresh already running?
 * @param {number} state.elapsedMs   Since the last refresh STARTED.
 * @param {number} state.intervalMs
 * @returns {'idle'|'skip'|'wait'|'refresh'}
 */
export function refreshDecision({ visible, inFlight, elapsedMs, intervalMs }) {
  // A hidden tab is not merely low priority, it is *free* to skip: nobody can see the
  // result, and the refresh on becoming visible again will be more current than
  // anything polled in the meantime.
  if (!visible) return 'idle';
  // One at a time. A slow request must not stack up behind itself — under a database
  // that has gone slow, that turns a screen refresh into a queue of them, and the
  // pile-up arrives exactly when the server can least afford it.
  if (inFlight) return 'skip';
  return elapsedMs >= intervalMs ? 'refresh' : 'wait';
}

/**
 * Call `refresh` periodically while the tab is visible.
 *
 * `refresh` is called with no arguments and its rejection is ignored — a failed
 * background poll must leave the screen exactly as it was (see the note on `refresh`
 * in pages/tickets/Detail.jsx). It is held in a ref so a caller that rebuilds the
 * function every render does not restart the timer on every render.
 *
 * @param {() => Promise<unknown>|void} refresh
 * @param {{ intervalMs?: number, enabled?: boolean }} [options]
 */
export function useLiveRefresh(refresh, { intervalMs = DEFAULT_REFRESH_MS, enabled = true } = {}) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  const inFlight = useRef(false);
  const lastStartedAt = useRef(0);

  useEffect(() => {
    if (!enabled) return undefined;

    const isVisible = () => typeof document === 'undefined' || document.visibilityState === 'visible';

    const run = () => {
      inFlight.current = true;
      lastStartedAt.current = Date.now();
      // `Promise.resolve` so a caller may hand over a synchronous function; `finally`
      // rather than `then` so a rejection still clears the flag — a refresh that threw
      // and left `inFlight` true would stop this screen updating for the rest of the
      // session, silently.
      Promise.resolve()
        .then(() => refreshRef.current?.())
        .catch(() => {})
        .finally(() => { inFlight.current = false; });
    };

    const tick = () => {
      const decision = refreshDecision({
        visible: isVisible(),
        inFlight: inFlight.current,
        elapsedMs: Date.now() - lastStartedAt.current,
        intervalMs,
      });
      if (decision === 'refresh') run();
    };

    const onVisibility = () => {
      // Straight to a refresh, not to the next tick: the tab has just been looked at,
      // and whatever is on it was last true whenever it was hidden.
      if (isVisible() && !inFlight.current) run();
    };

    const timer = setInterval(tick, 1_000);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // `refresh` is deliberately absent: it lives in a ref precisely so that changing
    // it does not tear down and rebuild the timer.
  }, [intervalMs, enabled]);
}
